import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { Product } from '../types/api';
import { getPriceLists } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from './Button';
import { DeleteButton } from './DeleteButton';
import { MagnifyingGlass, Plus } from 'phosphor-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './Dialog';

const PAGE_SIZE = 50;

const formatPrice = (price: number) =>
  `$${price.toLocaleString('es-CL', { minimumFractionDigits: 0 })}`;

export const ProductsSection: React.FC<{ onSaved?: () => void }> = ({ onSaved }) => {
  const api = useApi();
  const navigate = useNavigate();
  const { getAccessToken } = useAuth();
  const [priceListMap, setPriceListMap] = useState<Record<string, number>>({});

  const [products, setProducts] = useState<Product[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Inline name editing
  const [editingNameId, setEditingNameId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');
  const [savingName, setSavingName] = useState(false);

  // Inline price editing
  const [editingPriceId, setEditingPriceId] = useState<number | null>(null);
  const [editingPrice, setEditingPrice] = useState('');
  const [savingPrice, setSavingPrice] = useState(false);

  // Inline category editing
  const [savingCategory, setSavingCategory] = useState<number | null>(null);

  const UNIDADES_DE_NEGOCIO = ['COCINA CALIENTE', 'COCINA FRIA', 'ENSALADAS', 'SANDWICH'];


  // Create product dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', sku: '', price: '', unidadDeNegocio: '' });
  const [createError, setCreateError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await api.getProducts(search || undefined, PAGE_SIZE, page);
      setProducts(result.data || []);
      setTotal(result.pagination?.total ?? 0);
    } catch {
      setError('No se pudieron cargar los productos.');
    } finally {
      setLoading(false);
    }
  }, [search, page, api]);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  useEffect(() => {
    getAccessToken().then(token => getPriceLists(token)).then(({ lists }) => {
      const map: Record<string, number> = {};
      lists.forEach(l => { map[l.name] = l.id; });
      setPriceListMap(map);
    }).catch(() => {});
  }, [getAccessToken]);

  // Reset page on new search
  const handleSearch = () => {
    setPage(1);
    setSearch(searchInput);
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  // --- Name editing ---
  const startEditName = (product: Product) => {
    if (editingPriceId !== null) return;
    setEditingNameId(product.id);
    setEditingName(product.name);
  };

  const saveName = async (id: number) => {
    const trimmed = editingName.trim();
    if (!trimmed) { setEditingNameId(null); return; }
    try {
      setSavingName(true);
      const updated = await api.updateProduct(id, { name: trimmed });
      setProducts(prev => prev.map(p => p.id === id ? { ...p, name: updated.name } : p));
      onSaved?.();
    } catch {
      // revert silently
    } finally {
      setSavingName(false);
      setEditingNameId(null);
    }
  };

  // --- Price editing ---
  const startEditPrice = (product: Product) => {
    if (editingNameId !== null) return;
    setEditingPriceId(product.id);
    setEditingPrice(product.price.toString());
  };

  const savePrice = async (id: number) => {
    const price = parseFloat(editingPrice);
    if (isNaN(price) || price < 0) { setEditingPriceId(null); return; }
    try {
      setSavingPrice(true);
      await api.updateProduct(id, { price });
      setProducts(prev => prev.map(p => p.id === id ? { ...p, price } : p));
      onSaved?.();
    } catch {
      // revert silently
    } finally {
      setSavingPrice(false);
      setEditingPriceId(null);
    }
  };

  // --- Category editing ---
  const saveCategory = async (id: number, category: string | null) => {
    try {
      setSavingCategory(id);
      await api.updateProduct(id, { category });
      setProducts(prev => prev.map(p => p.id === id ? { ...p, category: category ?? undefined } : p));
      onSaved?.();
    } catch {
      // revert silently
    } finally {
      setSavingCategory(null);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await api.deleteProduct(id);
      setProducts(prev => prev.filter(p => p.id !== id));
      setTotal(prev => prev - 1);
    } catch (e: any) {
      const msg = e?.message || '';
      if (msg.includes('pedidos')) {
        setError('No se puede eliminar: el producto tiene pedidos asociados.');
      } else {
        setError('No se pudo eliminar el producto.');
      }
    }
  };

  const handleCreate = async () => {
    if (!createForm.name.trim()) { setCreateError('El nombre es requerido.'); return; }
    if (!createForm.sku.trim()) { setCreateError('El SKU es requerido.'); return; }
    try {
      setCreating(true);
      setCreateError(null);
      const created = await api.createProduct({
        name: createForm.name.trim().toUpperCase(),
        sku: createForm.sku.trim().toLowerCase(),
        price: parseFloat(createForm.price) || 0,
        unidadDeNegocio: createForm.unidadDeNegocio || undefined,
      });
      setProducts(prev => [created, ...prev]);
      setTotal(prev => prev + 1);
      setCreateOpen(false);
      setCreateForm({ name: '', sku: '', price: '', unidadDeNegocio: '' });
      onSaved?.();
    } catch (e: any) {
      setCreateError(e.message || 'Error al crear producto.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="flex flex-col h-full gap-4">
      {/* Search bar */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-subtle" />
          <input
            type="text"
            placeholder="Buscar por nombre o SKU..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
            className="w-full pl-9 pr-4 py-2 rounded-md border border-border bg-background text-sm font-sans outline-none focus:border-primary transition-colors"
          />
        </div>
        <Button variant="primary" size="sm" leftIcon={<Plus size={14} weight="bold" />} onClick={() => { setCreateForm({ name: '', sku: '', price: '', unidadDeNegocio: '' }); setCreateError(null); setCreateOpen(true); }}>
          Agregar producto
        </Button>
      </div>

      {error && (
        <p className="font-sans text-sm text-destructive">{error}</p>
      )}

      {products.length === 0 && !loading ? (
        <div className="flex flex-col items-center justify-center py-12 px-4 border border-dashed border-border rounded-lg">
          <p className="font-sans text-sm text-text-subtle text-center">
            {search ? 'No se encontraron productos.' : 'No hay productos.'}
          </p>
        </div>
      ) : (
        <>
          <div className="border border-border rounded-lg overflow-hidden flex-1 min-h-0 w-full">
            <table className="w-full text-sm font-sans">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left px-4 py-2 font-medium text-text-subtle">Nombre</th>
                  <th className="text-left px-4 py-2 font-medium text-text-subtle w-28">SKU</th>
                  <th className="text-right px-4 py-2 font-medium text-text-subtle w-28">Precio</th>
                  <th className="text-left px-4 py-2 font-medium text-text-subtle w-36">Unidad de Negocio</th>
                  <th className="text-left px-4 py-2 font-medium text-text-subtle w-40">Lista de precios</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => (
                  <tr
                    key={product.id}
                    className="border-b border-border last:border-b-0 hover:bg-muted/20"
                  >
                    {/* Name */}
                    <td className="px-4 py-2.5 text-text max-w-0">
                      {editingNameId === product.id ? (
                        <input
                          type="text"
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          onBlur={() => saveName(product.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveName(product.id);
                            if (e.key === 'Escape') setEditingNameId(null);
                          }}
                          disabled={savingName}
                          autoFocus
                          className="w-full px-2 py-1 rounded border border-primary bg-background text-sm outline-none"
                          onClick={(e) => e.stopPropagation()}
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); startEditName(product); }}
                          className="text-left text-text hover:text-primary hover:underline cursor-pointer transition-colors whitespace-normal break-words block"
                          title={product.name}
                        >
                          {product.name}
                        </button>
                      )}
                    </td>

                    {/* SKU */}
                    <td className="px-4 py-2.5 text-text-subtle whitespace-nowrap">{product.sku}</td>

                    {/* Price */}
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      {editingPriceId === product.id ? (
                        <input
                          type="number"
                          value={editingPrice}
                          onChange={(e) => setEditingPrice(e.target.value)}
                          onBlur={() => savePrice(product.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') savePrice(product.id);
                            if (e.key === 'Escape') setEditingPriceId(null);
                          }}
                          disabled={savingPrice}
                          autoFocus
                          className="w-28 px-2 py-1 rounded border border-primary bg-background text-sm text-right outline-none"
                          onClick={(e) => e.stopPropagation()}
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); startEditPrice(product); }}
                          className="text-text hover:text-primary hover:underline cursor-pointer transition-colors"
                        >
                          {formatPrice(product.price)}
                        </button>
                      )}
                    </td>

                    {/* Unidad de Negocio */}
                    <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                      <select
                        value={product.category || ''}
                        disabled={savingCategory === product.id}
                        onChange={(e) => saveCategory(product.id, e.target.value || null)}
                        className="text-xs font-sans border border-border rounded bg-background text-text px-2 py-1 outline-none focus:border-primary transition-colors"
                      >
                        <option value="">—</option>
                        {UNIDADES_DE_NEGOCIO.map(u => (
                          <option key={u} value={u}>{u}</option>
                        ))}
                      </select>
                    </td>

                    {/* Price lists */}
                    <td className="px-4 py-2.5 w-40">
                      {product.priceLists && product.priceLists.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {product.priceLists.map(pl => (
                            <button
                              key={pl}
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                const id = priceListMap[pl];
                                if (id) navigate(`/configuracion/listas-de-precios/${id}`);
                              }}
                              className="inline-block px-2 py-0.5 rounded-full bg-muted text-xs font-sans text-text-subtle border border-border whitespace-nowrap hover:bg-primary/10 hover:border-primary hover:text-primary transition-colors cursor-pointer"
                            >
                              {pl}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <span className="text-text-subtle/50 text-xs">—</span>
                      )}
                    </td>

                    {/* Delete */}
                    <td className="py-2.5 w-10 relative" onClick={(e) => e.stopPropagation()}>
                      <DeleteButton onDelete={() => handleDelete(product.id)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between text-sm text-text-subtle border-t border-border bg-background px-2 py-2 sticky bottom-0">
            <span>
              {total > 0 ? `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} de ${total}` : '0 productos'}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}
              >
                Anterior
              </Button>
              <span className="text-text">
                {page} / {Math.max(1, totalPages)}
              </span>
              <Button
                variant="ghost"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage(p => p + 1)}
              >
                Siguiente
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Create product dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="p-6">
          <DialogHeader className="mb-4">
            <DialogTitle>Agregar producto</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <input
              autoFocus
              type="text"
              placeholder="Nombre (ej: GOHAN CAMARÓN)"
              value={createForm.name}
              onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))}
              className="w-full px-3 py-2 text-sm font-sans border border-border rounded-md bg-background text-text placeholder:text-text-subtle focus:outline-none focus:border-primary transition-colors"
            />
            <input
              type="text"
              placeholder="SKU (ej: prococ354)"
              value={createForm.sku}
              onChange={e => setCreateForm(f => ({ ...f, sku: e.target.value }))}
              className="w-full px-3 py-2 text-sm font-sans border border-border rounded-md bg-background text-text placeholder:text-text-subtle focus:outline-none focus:border-primary transition-colors"
            />
            <input
              type="number"
              placeholder="Precio (sin IVA)"
              value={createForm.price}
              onChange={e => setCreateForm(f => ({ ...f, price: e.target.value }))}
              className="w-full px-3 py-2 text-sm font-sans border border-border rounded-md bg-background text-text placeholder:text-text-subtle focus:outline-none focus:border-primary transition-colors"
            />
            <select
              value={createForm.unidadDeNegocio}
              onChange={e => setCreateForm(f => ({ ...f, unidadDeNegocio: e.target.value }))}
              className="w-full px-3 py-2 text-sm font-sans border border-border rounded-md bg-background text-text focus:outline-none focus:border-primary transition-colors"
            >
              <option value="">Unidad de Negocio (opcional)</option>
              {UNIDADES_DE_NEGOCIO.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
            {createError && <p className="text-sm text-destructive">{createError}</p>}
          </div>
          <DialogFooter className="mt-6">
            <Button variant="ghost" onClick={() => setCreateOpen(false)} disabled={creating}>Cancelar</Button>
            <Button variant="primary" onClick={handleCreate} loading={creating}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
